/**
 * Admin section access for Users / Roles / User Types.
 *
 * - **God mode (`is_admin`):** full see + mutate for these sections (no `role_keys` required).
 * - **Non-admin:** keyed by session `user.role_keys` (view vs manage). The Admin shell is still
 *   `is_admin`-only in `App.tsx`, so the non-admin branch is for consistency / future use.
 */
import type { User } from '../types/user';

function keys(user: User | null): Set<string> {
  if (!user?.role_keys?.length) return new Set();
  return new Set(user.role_keys);
}

export function canSeeUsersAdmin(user: User | null): boolean {
  if (user?.is_admin) return true;
  const k = keys(user);
  return k.has('admin.users.view') || k.has('admin.users.manage');
}

export function canMutateUsersAdmin(user: User | null): boolean {
  if (user?.is_admin) return true;
  return keys(user).has('admin.users.manage');
}

export function canSeeRolesAdmin(user: User | null): boolean {
  if (user?.is_admin) return true;
  const k = keys(user);
  return k.has('admin.roles.view') || k.has('admin.roles.manage');
}

export function canMutateRolesAdmin(user: User | null): boolean {
  if (user?.is_admin) return true;
  return keys(user).has('admin.roles.manage');
}

export function canSeeUserTypesAdmin(user: User | null): boolean {
  if (user?.is_admin) return true;
  const k = keys(user);
  return k.has('admin.user_types.view') || k.has('admin.user_types.manage');
}

export function canMutateUserTypesAdmin(user: User | null): boolean {
  if (user?.is_admin) return true;
  return keys(user).has('admin.user_types.manage');
}
